import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Calendar as CalendarIcon, Plus, Users, MapPin, X, Loader2, Waves,
} from "lucide-react";

function ymd(dt) { return dt.toISOString().slice(0, 10); }
function hm(dt) { return dt.toISOString().slice(11, 16); }
function fmtPlanned(iso) {
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date(); tomorrow.setDate(today.getDate() + 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  if (sameDay(d, today)) return `Today · ${time}`;
  if (sameDay(d, tomorrow)) return `Tomorrow · ${time}`;
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }) + ` · ${time}`;
}

function SessionComposer({ beaches, onCreated, onClose }) {
  const { t } = useI18n();
  const [beachId, setBeachId] = useState(beaches?.[0]?.id || "playa-popoyo");
  const tomorrow = new Date(Date.now() + 24 * 60 * 60_000);
  tomorrow.setHours(7, 0, 0, 0);
  const [date, setDate] = useState(ymd(tomorrow));
  const [time, setTime] = useState("07:00");
  const [note, setNote] = useState("");
  const [maxJoiners, setMaxJoiners] = useState(6);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const planned = new Date(`${date}T${time}:00`);
      const { data } = await http.post("/sessions", {
        beach_id: beachId,
        planned_at: planned.toISOString(),
        note,
        max_joiners: Number(maxJoiners),
      });
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      queueMicrotask(() => setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message));
    } finally {
      queueMicrotask(() => setBusy(false));
    }
  };

  return (
    <form
      data-testid="session-composer"
      onSubmit={submit}
      className="rounded-2xl border border-[color:var(--coral)]/30 bg-[color:var(--coral)]/5 p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <p className="eyebrow text-[color:var(--coral)]">{t("session_new")}</p>
        <button type="button" onClick={onClose} className="text-[color:var(--ink-2)] hover:text-[color:var(--ink)]">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-1 block">{t("beach")}</label>
          <Select value={beachId} onValueChange={setBeachId}>
            <SelectTrigger data-testid="session-beach-trigger" className="rounded-xl bg-white h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {beaches?.map((b) => (
                <SelectItem key={b.id} value={b.id} data-testid={`session-beach-option-${b.id}`}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-1 block">{t("session_date")}</label>
            <Input data-testid="session-date" type="date" value={date} min={ymd(new Date())} onChange={(e) => setDate(e.target.value)} required className="rounded-xl bg-white h-10" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-1 block">{t("session_time")}</label>
            <Input data-testid="session-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} required className="rounded-xl bg-white h-10" />
          </div>
        </div>
      </div>
      <Textarea
        data-testid="session-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={t("session_note_placeholder")}
        maxLength={280}
        rows={2}
        className="rounded-xl bg-white"
      />
      <div className="flex items-center gap-2">
        <label className="text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] shrink-0">{t("session_max")}</label>
        <Input data-testid="session-max" type="number" min={1} max={50} value={maxJoiners} onChange={(e) => setMaxJoiners(e.target.value)} className="rounded-xl bg-white h-9 w-20" />
        <Button
          type="submit"
          data-testid="session-submit"
          disabled={busy}
          className="ml-auto pill bg-[color:var(--coral)] hover:bg-[color:var(--coral-2)] text-white"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
          {t("session_create")}
        </Button>
      </div>
      {err && <p data-testid="session-err" className="text-xs text-[color:var(--emergency)]">{err}</p>}
    </form>
  );
}

function SessionCard({ session, currentUser, onJoin, onLeave, onDelete }) {
  const { t } = useI18n();
  const joined = !!currentUser?.id && (session.joiners || []).some((j) => j.id === currentUser.id);
  const isHost = currentUser?.id && session.host_id === currentUser.id;
  const isFull = (session.joiners || []).length >= session.max_joiners;

  return (
    <div data-testid={`session-card-${session.id}`} className="rounded-2xl bg-white border border-[color:var(--hairline)] p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>{fmtPlanned(session.planned_at)}</p>
          <p className="text-xs text-[color:var(--ink-2)] mt-0.5 flex items-center gap-1">
            <MapPin className="w-3 h-3 text-[color:var(--coral)]" />
            {session.beach_name}
          </p>
        </div>
        <span
          className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 ${
            isFull ? "bg-[color:var(--emergency)] text-white" : "bg-[color:var(--teal)]/10 text-[color:var(--teal)]"
          }`}
        >
          <Users className="inline w-3 h-3 -mt-0.5 mr-0.5" />
          {(session.joiners || []).length}/{session.max_joiners}
        </span>
      </div>

      {session.note && (
        <p className="text-sm text-[color:var(--ink)] leading-relaxed mb-2">{session.note}</p>
      )}

      <div className="flex items-center gap-1.5 flex-wrap mb-3">
        {(session.joiners || []).slice(0, 5).map((j, i) => (
          <span
            key={j.id}
            data-testid={`session-joiner-${session.id}-${i}`}
            className={`pill px-2 py-0.5 text-[11px] ${j.id === session.host_id ? "bg-[color:var(--navy)] text-white" : "bg-[color:var(--sand-2)] text-[color:var(--ink)]"}`}
          >
            {j.id === session.host_id && "⭐ "}{j.name}
          </span>
        ))}
        {(session.joiners || []).length > 5 && (
          <span className="text-[11px] text-[color:var(--ink-2)]">+{session.joiners.length - 5}</span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {!currentUser?.id ? (
          <p className="text-xs text-[color:var(--ink-2)] italic">{t("session_signin_to_join")}</p>
        ) : joined ? (
          <Button
            data-testid={`session-leave-${session.id}`}
            size="sm" variant="outline"
            onClick={() => onLeave(session.id)}
            className="pill"
          >
            {t("session_leave")}
          </Button>
        ) : (
          <Button
            data-testid={`session-join-${session.id}`}
            size="sm"
            onClick={() => onJoin(session.id)}
            disabled={isFull}
            className="pill bg-[color:var(--teal)] hover:bg-[color:var(--teal)]/90 text-white"
          >
            <Waves className="w-3.5 h-3.5 mr-1" />
            {isFull ? t("session_full") : t("session_join")}
          </Button>
        )}
        {isHost && (
          <Button
            data-testid={`session-delete-${session.id}`}
            size="sm" variant="ghost"
            onClick={() => onDelete(session.id)}
            className="text-xs text-[color:var(--ink-2)] hover:text-[color:var(--emergency)] ml-auto"
          >
            {t("session_delete")}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function SurfSessions({ beaches, onOpenAuth }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const sessionsQ = useQuery({
    queryKey: ["sessions"],
    queryFn: async () => (await http.get("/sessions", { params: { upcoming: true } })).data,
    refetchInterval: 30_000,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["sessions"] });

  const join = async (id) => { try { await http.post(`/sessions/${id}/join`); } catch (_e) { /* ignore */ } refresh(); };
  const leave = async (id) => { try { await http.post(`/sessions/${id}/leave`); } catch (_e) { /* ignore */ } refresh(); };
  const del = async (id) => {
    if (!window.confirm(t("session_delete_confirm"))) return;
    try { await http.delete(`/sessions/${id}`); } catch (_e) { /* ignore */ }
    refresh();
  };

  const sessions = sessionsQ.data || [];

  return (
    <section data-testid="surf-sessions" className="clay p-6 md:p-7">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[color:var(--coral)]/10 text-[color:var(--coral)] flex items-center justify-center">
            <CalendarIcon className="w-6 h-6" />
          </div>
          <div>
            <p className="eyebrow">{t("sessions_eyebrow")}</p>
            <h3 className="text-2xl font-semibold mt-0.5" style={{ fontFamily: "Outfit" }}>{t("sessions_title")}</h3>
            <p className="text-xs text-[color:var(--ink-2)] mt-0.5">{t("sessions_subtitle")}</p>
          </div>
        </div>
        {user?.id ? (
          <Button
            data-testid="session-add-btn"
            onClick={() => setShowForm((s) => !s)}
            className="pill bg-[color:var(--coral)] hover:bg-[color:var(--coral-2)] text-white"
          >
            <Plus className="w-4 h-4 mr-1" />
            {t("session_new")}
          </Button>
        ) : (
          <Button
            data-testid="session-login-cta"
            onClick={() => onOpenAuth?.()}
            variant="outline"
            className="pill"
          >
            {t("session_signin_to_post")}
          </Button>
        )}
      </div>

      {showForm && user?.id && (
        <div className="mb-4">
          <SessionComposer beaches={beaches} onCreated={refresh} onClose={() => setShowForm(false)} />
        </div>
      )}

      {sessions.length === 0 ? (
        <p data-testid="sessions-empty" className="text-sm text-[color:var(--ink-2)] italic text-center py-6">
          {t("sessions_empty")}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {sessions.map((s) => (
            <SessionCard
              key={s.id}
              session={s}
              currentUser={user}
              onJoin={join}
              onLeave={leave}
              onDelete={del}
            />
          ))}
        </div>
      )}
    </section>
  );
}
